import type { AdminAsset, AssetUsageLocation, UpdateAssetRequest } from '@diana/contracts';
import { ArrowLeft } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import { CollectionSearch, Select } from '../../ui/index.js';

import { MediaCard } from './MediaCard.js';
import './MediaLibrary.css';

export type MediaActions = {
  loadUsages(id: string): Promise<AssetUsageLocation[]>;
  updateAsset(id: string, request: UpdateAssetRequest): Promise<AdminAsset>;
  archiveAsset(id: string, expectedRevision: number): Promise<AdminAsset>;
};

export function MediaLibrary({ assets }: { assets: readonly AdminAsset[] } & MediaActions) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const latestParams = useRef(params);
  latestParams.current = params;
  const search = (params.get('search') ?? '').slice(0, 100);
  const orientation = valid('orientation', ['All', 'Landscape', 'Portrait', 'Square']);
  const usage = valid('usage', ['All', 'Used', 'Unused']);
  const format = valid('format', ['All', 'jpeg', 'webp']);
  const archive = valid('archive', ['All', 'Current', 'Archived']);
  const missing = params.get('missingAlt') === '1';
  function valid(key: string, choices: string[]) {
    const value = params.get(key) ?? 'All';
    return choices.includes(value) ? value : 'All';
  }
  function change(values: Record<string, string>, replace = false) {
    const next = new URLSearchParams(latestParams.current);
    for (const [key, value] of Object.entries(values)) {
      if (!value || value === 'All') next.delete(key);
      else next.set(key, value);
    }
    latestParams.current = next;
    setParams(next, { replace });
  }
  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return assets.filter((asset) => {
      const totalUsage = asset.usage.draft + asset.usage.releases;
      return (
        (!query ||
          [asset.alt, asset.originalFilename, asset.sourceType, asset.format]
            .join(' ')
            .toLocaleLowerCase()
            .includes(query)) &&
        (archive === 'All' ||
          (archive === 'Archived' ? asset.archivedAt !== null : asset.archivedAt === null)) &&
        (format === 'All' || asset.format === format) &&
        (usage === 'All' || (usage === 'Used' ? totalUsage > 0 : totalUsage === 0)) &&
        (!missing || !asset.alt.trim()) &&
        orientationMatches(asset, orientation)
      );
    });
  }, [archive, assets, format, missing, orientation, search, usage]);
  const primary = missing
    ? 'Missing alt text'
    : usage === 'Used'
      ? 'In use'
      : usage === 'Unused'
        ? 'Not used'
        : 'All';
  return (
    <article className="editor media-library">
      <MediaHeader title="Media" backTo="/site" backLabel="Back to Site" />
      <div className="media-library__toolbar" aria-label="Media filters">
        <CollectionSearch
          className="media-library__search"
          label="Search media"
          value={search}
          maxLength={100}
          placeholder="File name or description"
          onChange={(event) => change({ search: event.currentTarget.value.slice(0, 100) }, true)}
        />
        <div className="media-library__chips" aria-label="Quick media filters">
          {['All', 'In use', 'Not used', 'Missing alt text'].map((chip) => (
            <button
              key={chip}
              type="button"
              aria-pressed={primary === chip}
              onClick={() =>
                change({
                  usage: chip === 'In use' ? 'Used' : chip === 'Not used' ? 'Unused' : 'All',
                  missingAlt: chip === 'Missing alt text' ? '1' : '',
                })
              }
            >
              {chip}
            </button>
          ))}
        </div>
        <details className="media-library__advanced">
          <summary>Advanced filters</summary>
          <div className="media-library__filters">
            <MediaFilter
              label="Orientation"
              value={orientation}
              options={['All', 'Landscape', 'Portrait', 'Square']}
              onChange={(value) => change({ orientation: value })}
            />
            <MediaFilter
              label="Usage"
              value={usage}
              options={['All', 'Used', 'Unused']}
              onChange={(value) => change({ usage: value, missingAlt: '' })}
            />
            <MediaFilter
              label="Format"
              value={format}
              options={['All', 'jpeg', 'webp']}
              onChange={(value) => change({ format: value })}
            />
            <MediaFilter
              label="Archive"
              value={archive}
              options={['All', 'Current', 'Archived']}
              onChange={(value) => change({ archive: value })}
            />
          </div>
        </details>
        <p className="media-library__note">
          {filtered.length} of {assets.length} assets
        </p>
      </div>
      <p className="media-library__note">
        Upload photographs from the content editor where you want to use them.
      </p>
      <section className="media-library__grid" aria-label="Media library">
        {filtered.map((asset) => (
          <MediaCard key={asset.id} asset={asset} search={location.search} />
        ))}
      </section>
      {filtered.length === 0 ? (
        <p className="media-library__note">No media matches this search.</p>
      ) : null}
    </article>
  );
}

export function MediaHeader({
  title,
  backTo,
  backLabel,
}: {
  title: string;
  backTo: string;
  backLabel: string;
}) {
  return (
    <header className="media-library__heading">
      <Link className="dui-back-link media-library__back" to={backTo} aria-label={backLabel}>
        <ArrowLeft size={18} aria-hidden="true" />
        <span>{backLabel}</span>
      </Link>
      <h1>{title}</h1>
    </header>
  );
}

function MediaFilter({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange(value: string): void;
}) {
  return (
    <label>
      <span>{label}</span>
      <Select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option === 'jpeg' ? 'JPG' : option === 'webp' ? 'WebP' : option}
          </option>
        ))}
      </Select>
    </label>
  );
}

function orientationMatches(asset: AdminAsset, orientation: string) {
  if (orientation === 'All') return true;
  if (!asset.width || !asset.height) return false;
  if (orientation === 'Square') return asset.width === asset.height;
  return orientation === 'Landscape' ? asset.width > asset.height : asset.height > asset.width;
}
